import { useFeatureAccess } from "../../../access-control/hooks/useFeatureAccess";
import { FEATURE_KEYS } from "../../../access-control/models/role-feature.models";
import { themeAtom } from "../../../shared/atoms";
import {
  CRON_JOB_LOG_ENVIRONMENTS,
  CRON_MONITORING_JOB_GROUPS,
  CRON_MONITORING_JOBS,
  CronJobLogDetail,
  CronJobLogEnvironment,
  CronJobLogListItem,
  CronJobLogsResponse,
  CronJobLogSortBy,
  CronJobLogSortDirection,
  CronJobTypeSummary,
  CronMonitoringJobDefinition,
} from "../../models/admin-monitoring.models";
import {
  getAdminMonitoringErrorMessage,
  getCronJobLogDetail,
  getCronJobLogs,
} from "../../services/admin-monitoring.service";
import { adminStyles } from "../../styles/admin.styles";
import { getDefaultAdminMonitoringEnvironment } from "../../utils/admin-monitoring-environment.utils";
import {
  formatCronDateTime,
  formatCronDay,
  formatCronJson,
  formatCronTime,
  getCronJobDefinition,
  getCronLogMetrics,
} from "../../utils/admin-monitoring.utils";
import HoldCleaningMonitoringPanel from "./HoldCleaningMonitoringPanel";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

const PAGE_SIZE = 50;
const DEFAULT_ENVIRONMENT = getDefaultAdminMonitoringEnvironment();

const metricToneClasses = {
  success: "bg-[#CCE4E6] text-[#007681]",
  warning: "bg-[#FFF8E1] text-[#946200]",
  danger: "bg-[#FFEAED] text-[#BF0002]",
  neutral: "bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
};

const getJobTypeMap = (jobTypes: CronJobTypeSummary[]) =>
  jobTypes.reduce<Record<string, CronJobTypeSummary>>((map, jobType) => {
    map[jobType.name] = jobType;
    return map;
  }, {});

const getNavJobs = (
  jobTypes: CronJobTypeSummary[],
  canViewGeneric: boolean,
  canViewHoldCleaning: boolean,
): CronMonitoringJobDefinition[] => {
  const knownNames = new Set(CRON_MONITORING_JOBS.map((job) => job.name));
  const unknownJobs = canViewGeneric
    ? jobTypes.filter((jobType) => !knownNames.has(jobType.name)).map((jobType) => getCronJobDefinition(jobType.name))
    : [];

  const configured = CRON_MONITORING_JOBS.filter((job) =>
    job.requiredFeatureKey === FEATURE_KEYS.HOLD_CLEANING_ADMIN ? canViewHoldCleaning : canViewGeneric,
  );
  return [...configured, ...unknownJobs];
};

const getEnvironmentFromSearchParam = (value: string | null): CronJobLogEnvironment => {
  const normalized = value?.trim().toUpperCase();

  if (normalized === "QS") {
    return "QAS";
  }

  const match = CRON_JOB_LOG_ENVIRONMENTS.find((environment) => environment.value === normalized);
  return match?.value ?? DEFAULT_ENVIRONMENT;
};

const getSelectedJobFromSearchParam = (value: string | null): string | undefined => {
  const normalized = value?.trim().toUpperCase();

  if (!normalized || normalized === "ALL") {
    return undefined;
  }

  return normalized;
};

const MonitoringNav = ({
  selectedName,
  jobTypes,
  totalLogs,
  onSelectName,
  canViewGeneric,
  canViewHoldCleaning,
}: {
  selectedName?: string;
  jobTypes: CronJobTypeSummary[];
  totalLogs: number;
  onSelectName: (name?: string) => void;
  canViewGeneric: boolean;
  canViewHoldCleaning: boolean;
}) => {
  const jobTypeMap = useMemo(() => getJobTypeMap(jobTypes), [jobTypes]);
  const navJobs = useMemo(
    () => getNavJobs(jobTypes, canViewGeneric, canViewHoldCleaning),
    [canViewGeneric, canViewHoldCleaning, jobTypes],
  );

  return (
    <aside data-test="admin-monitoring-secondary-nav" className={adminStyles.secondaryNav.shell}>
      <div className={adminStyles.secondaryNav.list}>
        {canViewGeneric && (
          <div className={twMerge(adminStyles.secondaryNav.section, "pt-0")}>
            <button
              type="button"
              data-test="admin-monitoring-nav-all"
              data-state={!selectedName ? "active" : "inactive"}
              className={twMerge(
                adminStyles.secondaryNav.item,
                "flex items-center justify-between gap-3",
                !selectedName ? adminStyles.secondaryNav.itemActive : adminStyles.secondaryNav.itemInactive,
              )}
              onClick={() => onSelectName(undefined)}
            >
              <span>All cron jobs</span>
              <span className="text-[10px] opacity-70">{totalLogs.toLocaleString()}</span>
            </button>
          </div>
        )}

        {CRON_MONITORING_JOB_GROUPS.map((group) => {
          const jobs = navJobs.filter((job) => job.group === group);

          if (jobs.length === 0) {
            return null;
          }

          return (
            <div key={group} className={adminStyles.secondaryNav.section}>
              <p className={adminStyles.secondaryNav.group}>{group}</p>
              <div className="space-y-1">
                {jobs.map((job) => {
                  const summary = jobTypeMap[job.name];
                  const isActive = selectedName === job.name;

                  return (
                    <button
                      key={job.name}
                      type="button"
                      data-test={`admin-monitoring-nav-${job.name}`}
                      data-state={isActive ? "active" : "inactive"}
                      className={twMerge(
                        adminStyles.secondaryNav.item,
                        "flex items-center justify-between gap-3",
                        isActive ? adminStyles.secondaryNav.itemActive : adminStyles.secondaryNav.itemInactive,
                      )}
                      onClick={() => onSelectName(job.name)}
                    >
                      <span className="min-w-0 truncate">{job.title}</span>
                      <span className={twMerge("text-[10px] opacity-70", summary?.errorCount ? "text-[#BF0002]" : "")}>
                        {(summary?.total ?? 0).toLocaleString()}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
};

const MetricTile = ({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string | number;
  tone?: keyof typeof metricToneClasses;
}) => (
  <div className={twMerge(adminStyles.card.indicatorCompact, metricToneClasses[tone])}>
    <p className="truncate text-[10px] font-semibold uppercase opacity-75">{label}</p>
    <p className="mt-2 break-words text-4xl font-bold leading-none">{value}</p>
  </div>
);

const DashboardTiles = ({ data }: { data: CronJobLogsResponse }) => (
  <div className="grid grid-cols-2 gap-3 md:grid-cols-3" data-test="admin-monitoring-dashboard">
    <MetricTile label="Logs" value={data.summary.totalLogs.toLocaleString()} />
    <MetricTile
      label="Errors"
      value={data.summary.logsWithErrors.toLocaleString()}
      tone={data.summary.logsWithErrors > 0 ? "danger" : "success"}
    />
    <MetricTile label="Live runs" value={data.summary.liveRuns.toLocaleString()} tone="warning" />
    <MetricTile label="Dry runs" value={data.summary.dryRuns.toLocaleString()} tone="success" />
    <MetricTile label="Types" value={data.summary.jobTypes.toLocaleString()} />
    <MetricTile label="Latest" value={formatCronTime(data.summary.latestCreated)} />
  </div>
);

const LatestJobDashboard = ({ selectedLog }: { selectedLog?: CronJobLogListItem }) => {
  const metrics = getCronLogMetrics(selectedLog);
  const definition = selectedLog ? getCronJobDefinition(selectedLog.name) : undefined;

  return (
    <section className={twMerge(adminStyles.card.surface, "px-4 py-4")} data-test="admin-monitoring-job-dashboard">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold">{definition?.title ?? "Latest cron job"}</p>
          <p className="mt-1 max-w-4xl text-xs leading-5 opacity-75">{definition?.focus ?? "Select a log entry."}</p>
        </div>
        {selectedLog && (
          <span
            className={twMerge(
              "inline-flex items-center gap-1 border px-2 py-1 text-xs font-semibold",
              selectedLog.hasErrors ? "border-[#F59692] text-[#BF0002]" : "border-[#99C8CD] text-[#007681]",
            )}
          >
            {selectedLog.hasErrors ? (
              <ExclamationTriangleIcon className="h-4 w-4" />
            ) : (
              <CheckCircleIcon className="h-4 w-4" />
            )}
            {selectedLog.hasErrors ? `${selectedLog.errorCount} error items` : "No errors"}
          </span>
        )}
      </div>

      {metrics.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {metrics.map((metric) => (
            <MetricTile key={metric.key} label={metric.label} value={metric.value} tone={metric.tone} />
          ))}
        </div>
      ) : (
        <div className="border-greyscale-20 border border-dashed px-4 py-5 text-sm opacity-70 dark:border-gray-700">
          No numeric metrics were captured for the latest entry.
        </div>
      )}
    </section>
  );
};

const TrendChart = ({ data }: { data: CronJobLogsResponse }) => {
  const maxTotal = Math.max(...data.dailyCounts.map((item) => item.total), 1);

  return (
    <section className={twMerge(adminStyles.card.surface, "p-4")} data-test="admin-monitoring-trend">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-semibold">14-day log volume</p>
        <div className="flex items-center gap-4 text-xs opacity-75">
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 bg-green" />
            Logs
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 bg-[#BF0002]" />
            Errors
          </span>
        </div>
      </div>

      {data.dailyCounts.length > 0 ? (
        <div className="flex h-40 items-end gap-2 overflow-x-auto pb-1">
          {data.dailyCounts.map((item) => {
            const height = Math.max(10, Math.round((item.total / maxTotal) * 100));
            const errorHeight = item.total > 0 ? Math.max(0, Math.round((item.errors / item.total) * height)) : 0;

            return (
              <div key={item.day} className="flex min-w-[46px] flex-1 flex-col items-center gap-2">
                <div className="relative flex h-28 w-full max-w-[44px] items-end bg-[#E9F4F3] dark:bg-gray-700">
                  <div className="w-full bg-green" style={{ height: `${height}%` }} title={`${item.total} logs`} />
                  {item.errors > 0 && (
                    <div
                      className="absolute bottom-0 left-0 w-full bg-[#BF0002]"
                      style={{ height: `${errorHeight}%` }}
                      title={`${item.errors} error logs`}
                    />
                  )}
                </div>
                <span className="text-[10px] opacity-70">{formatCronDay(item.day)}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="border-greyscale-20 border border-dashed px-4 py-5 text-sm opacity-70 dark:border-gray-700">
          No recent logs in this filter.
        </div>
      )}
    </section>
  );
};

const Controls = ({
  sortBy,
  sortDirection,
  runFilter,
  loading,
  onSortByChange,
  onSortDirectionChange,
  onRunFilterChange,
  onRefresh,
}: {
  sortBy: CronJobLogSortBy;
  sortDirection: CronJobLogSortDirection;
  runFilter: "all" | "true" | "false";
  loading: boolean;
  onSortByChange: (sortBy: CronJobLogSortBy) => void;
  onSortDirectionChange: (sortDirection: CronJobLogSortDirection) => void;
  onRunFilterChange: (runFilter: "all" | "true" | "false") => void;
  onRefresh: () => void;
}) => (
  <div className={twMerge(adminStyles.card.surface, "flex flex-wrap items-end justify-between gap-3 p-4")}>
    <div className="flex flex-wrap gap-3">
      <label className="text-xs font-semibold uppercase">
        <span className="mb-1 block opacity-70">Sort</span>
        <select
          value={sortBy}
          data-test="admin-monitoring-cron-sort-select"
          className="h-10 border border-gray-300 bg-transparent px-3 text-sm normal-case outline-none focus:border-green dark:border-gray-700"
          onChange={(event) => onSortByChange(event.target.value as CronJobLogSortBy)}
        >
          <option value="created">Created</option>
          <option value="name">Type</option>
          <option value="jobFrequency">Frequency</option>
          <option value="run">Mode</option>
        </select>
      </label>

      <label className="text-xs font-semibold uppercase">
        <span className="mb-1 block opacity-70">Direction</span>
        <select
          value={sortDirection}
          data-test="admin-monitoring-cron-sort-direction-select"
          className="h-10 border border-gray-300 bg-transparent px-3 text-sm normal-case outline-none focus:border-green dark:border-gray-700"
          onChange={(event) => onSortDirectionChange(event.target.value as CronJobLogSortDirection)}
        >
          <option value="desc">Descending</option>
          <option value="asc">Ascending</option>
        </select>
      </label>

      <label className="text-xs font-semibold uppercase">
        <span className="mb-1 block opacity-70">Mode</span>
        <select
          value={runFilter}
          data-test="admin-monitoring-cron-run-filter-select"
          className="h-10 border border-gray-300 bg-transparent px-3 text-sm normal-case outline-none focus:border-green dark:border-gray-700"
          onChange={(event) => onRunFilterChange(event.target.value as "all" | "true" | "false")}
        >
          <option value="all">All modes</option>
          <option value="true">Live run</option>
          <option value="false">Dry run</option>
        </select>
      </label>
    </div>

    <button
      type="button"
      className="inline-flex h-10 items-center gap-2 bg-green px-4 text-sm font-semibold text-white transition-colors hover:bg-[#166b60] disabled:opacity-50"
      disabled={loading}
      onClick={onRefresh}
    >
      <ArrowPathIcon className={twMerge("h-5 w-5", loading && "animate-spin")} />
      Refresh
    </button>
  </div>
);

const EnvironmentSelector = ({
  value,
  loading,
  onChange,
}: {
  value: CronJobLogEnvironment;
  loading: boolean;
  onChange: (value: CronJobLogEnvironment) => void;
}) => (
  <label className="text-xs font-semibold uppercase">
    <span className="mb-1 block opacity-70">Environment</span>
    <select
      value={value}
      disabled={loading}
      data-test="admin-monitoring-cron-environment-select"
      className="h-10 border border-gray-300 bg-transparent px-3 text-sm normal-case outline-none focus:border-green disabled:opacity-60 dark:border-gray-700"
      onChange={(event) => onChange(event.target.value as CronJobLogEnvironment)}
    >
      {CRON_JOB_LOG_ENVIRONMENTS.map((environment) => (
        <option key={environment.value} value={environment.value}>
          {environment.label}
        </option>
      ))}
    </select>
  </label>
);

const LogList = ({
  entries,
  selectedLogId,
  onSelectLog,
}: {
  entries: CronJobLogListItem[];
  selectedLogId: string | null;
  onSelectLog: (logId: string) => void;
}) => {
  if (entries.length === 0) {
    return (
      <div className="border-greyscale-20 border border-dashed px-4 py-8 text-center text-sm opacity-70 dark:border-gray-700">
        No cron job logs match this filter.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-test="admin-monitoring-log-list">
      {entries.map((entry) => {
        const definition = getCronJobDefinition(entry.name);
        const isSelected = entry.id === selectedLogId;
        const metrics = getCronLogMetrics(entry).slice(0, 3);

        return (
          <button
            key={entry.id}
            type="button"
            data-test={`admin-monitoring-log-${entry.id}`}
            data-state={isSelected ? "active" : "inactive"}
            className={twMerge(
              "w-full p-4 text-left shadow-sm transition-colors",
              isSelected
                ? "bg-[#E9F4F3] shadow-[inset_3px_0_0_#1F8475] dark:bg-teal-900/40"
                : "bg-white hover:bg-[#F7FBFA] dark:bg-gray-800 dark:hover:bg-gray-700",
            )}
            onClick={() => onSelectLog(entry.id)}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{definition.title}</p>
                <p className="mt-1 line-clamp-2 text-xs leading-5 opacity-75">{entry.summary ?? "No summary."}</p>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2 text-[11px] font-semibold">
                <span className="border border-gray-300 px-2 py-1 dark:border-gray-700">
                  {formatCronDateTime(entry.created)}
                </span>
                <span
                  className={twMerge(
                    "border px-2 py-1",
                    entry.run ? "border-[#FFE082] text-[#946200]" : "border-[#99C8CD] text-[#007681]",
                  )}
                >
                  {entry.run ? "Live" : "Dry run"}
                </span>
                <span
                  className={twMerge(
                    "border px-2 py-1",
                    entry.hasErrors ? "border-[#F59692] text-[#BF0002]" : "border-[#99C8CD] text-[#007681]",
                  )}
                >
                  {entry.hasErrors ? `${entry.errorCount} error items` : "OK"}
                </span>
              </div>
            </div>

            {metrics.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {metrics.map((metric) => (
                  <span key={metric.key} className="border border-gray-200 px-2 py-1 text-[11px] dark:border-gray-700">
                    <span className="opacity-70">{metric.label}</span>{" "}
                    <span className="font-semibold">{metric.value}</span>
                  </span>
                ))}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
};

const Pagination = ({ data, onPageChange }: { data: CronJobLogsResponse; onPageChange: (page: number) => void }) => (
  <div className="border-greyscale-20 flex flex-wrap items-center justify-between gap-3 border-t pt-4 text-sm dark:border-gray-700">
    <span className="opacity-75">
      Page {data.totalPages === 0 ? 0 : data.page} of {data.totalPages} - {data.totalRecords.toLocaleString()} logs
    </span>
    <div className="flex gap-2">
      <button
        type="button"
        disabled={!data.prevPage}
        className="inline-flex h-9 items-center gap-1 border border-gray-300 px-3 font-semibold disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700"
        onClick={() => data.prevPage && onPageChange(data.prevPage)}
      >
        <ChevronLeftIcon className="h-4 w-4" />
        Prev
      </button>
      <button
        type="button"
        disabled={!data.nextPage}
        className="inline-flex h-9 items-center gap-1 border border-gray-300 px-3 font-semibold disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700"
        onClick={() => data.nextPage && onPageChange(data.nextPage)}
      >
        Next
        <ChevronRightIcon className="h-4 w-4" />
      </button>
    </div>
  </div>
);

const DetailSection = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="border-greyscale-20 border-t pt-4 dark:border-gray-700">
    <h3 className="mb-2 text-sm font-semibold">{title}</h3>
    {children}
  </section>
);

const DetailPanel = ({
  selectedLog,
  detail,
  loading,
}: {
  selectedLog?: CronJobLogListItem;
  detail: CronJobLogDetail | null;
  loading: boolean;
}) => {
  const theme = useAtomValue(themeAtom);
  const active = detail ?? selectedLog;

  if (!active) {
    return (
      <aside className={twMerge(adminStyles.card.surface, "p-5", theme.home.tailwind.panelBackground)}>
        <p className="text-sm font-semibold">Log detail</p>
        <p className="mt-2 text-sm opacity-70">Select a cron job log.</p>
      </aside>
    );
  }

  const definition = getCronJobDefinition(active.name);
  const metrics = getCronLogMetrics(detail ?? active);

  return (
    <aside
      data-test="admin-monitoring-detail"
      className={twMerge(adminStyles.card.surface, "min-h-0 p-5", theme.home.tailwind.panelBackground)}
    >
      <div className="mb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold">{definition.title}</p>
            <p className="mt-1 text-xs opacity-75">{active.name}</p>
          </div>
          {loading && <ArrowPathIcon className="h-5 w-5 animate-spin text-green" />}
        </div>
        <p className="mt-3 text-sm leading-6">{active.summary ?? "No summary."}</p>
      </div>

      <div className="space-y-5">
        <DetailSection title="Run">
          <dl className="grid grid-cols-2 gap-3 text-xs">
            <InfoItem label="Created" value={formatCronDateTime(active.created)} />
            <InfoItem label="Mode" value={active.run ? "Live run" : "Dry run"} />
            <InfoItem label="Frequency" value={active.jobFrequency ?? "N/A"} />
            <InfoItem label="Errors" value={active.hasErrors ? String(active.errorCount) : "0"} />
          </dl>
        </DetailSection>

        <DetailSection title="Indicators">
          {metrics.length > 0 ? (
            <div className="grid grid-cols-2 gap-2">
              {metrics.map((metric) => (
                <MetricTile key={metric.key} label={metric.label} value={metric.value} tone={metric.tone} />
              ))}
            </div>
          ) : (
            <p className="text-sm opacity-70">No indicators found.</p>
          )}
        </DetailSection>

        <DetailSection title="Details JSON">
          <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words bg-[#F7FBFA] p-3 text-xs leading-5 dark:bg-gray-900">
            {detail ? formatCronJson(detail.details) : "Loading..."}
          </pre>
        </DetailSection>

        <DetailSection title="Errors JSON">
          <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words bg-[#F7FBFA] p-3 text-xs leading-5 dark:bg-gray-900">
            {detail ? formatCronJson(detail.errors) : "Loading..."}
          </pre>
        </DetailSection>
      </div>
    </aside>
  );
};

const InfoItem = ({ label, value }: { label: string; value: string }) => (
  <div className="min-w-0 bg-[#F7FBFA] px-3 py-2 dark:bg-gray-900">
    <dt className="truncate text-[10px] font-semibold uppercase opacity-70">{label}</dt>
    <dd className="mt-1 break-words font-semibold">{value}</dd>
  </div>
);

const CronJobsMonitoringPanel = () => {
  const theme = useAtomValue(themeAtom);
  const { hasFeature } = useFeatureAccess();
  const canViewGeneric = hasFeature(FEATURE_KEYS.ADMIN_DEBUG_MONITORING);
  const canViewHoldCleaning = hasFeature(FEATURE_KEYS.HOLD_CLEANING_ADMIN);
  const [searchParams, setSearchParams] = useSearchParams();
  const environment = useMemo(() => getEnvironmentFromSearchParam(searchParams.get("environment")), [searchParams]);
  const requestedName = useMemo(() => getSelectedJobFromSearchParam(searchParams.get("job")), [searchParams]);
  const selectedName = useMemo(() => {
    if (requestedName === "HOLD_CLEANING_REFRESH") return canViewHoldCleaning ? requestedName : undefined;
    if (requestedName)
      return canViewGeneric ? requestedName : canViewHoldCleaning ? "HOLD_CLEANING_REFRESH" : undefined;
    return canViewGeneric ? undefined : canViewHoldCleaning ? "HOLD_CLEANING_REFRESH" : undefined;
  }, [canViewGeneric, canViewHoldCleaning, requestedName]);
  const isHoldCleaning = selectedName === "HOLD_CLEANING_REFRESH";
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<CronJobLogSortBy>("created");
  const [sortDirection, setSortDirection] = useState<CronJobLogSortDirection>("desc");
  const [runFilter, setRunFilter] = useState<"all" | "true" | "false">("all");
  const [data, setData] = useState<CronJobLogsResponse | null>(null);
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CronJobLogDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const expectedJob = selectedName ?? "all";
    if (searchParams.get("environment") === environment && (searchParams.get("job") ?? "all") === expectedJob) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("environment", environment);
    nextSearchParams.set("job", expectedJob);
    setSearchParams(nextSearchParams, { replace: true });
  }, [environment, searchParams, selectedName, setSearchParams]);

  useEffect(() => {
    setSelectedLogId(null);
    setDetail(null);
    setPage(1);
  }, [environment, selectedName]);

  const loadLogs = useCallback(async () => {
    if (isHoldCleaning) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorMessage(null);

    try {
      const response = await getCronJobLogs({
        environment,
        page,
        pageSize: PAGE_SIZE,
        name: selectedName,
        run: runFilter === "all" ? undefined : runFilter === "true",
        sortBy,
        sortDirection,
      });
      setData(response);
    } catch (error) {
      setErrorMessage(getAdminMonitoringErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [environment, isHoldCleaning, page, runFilter, selectedName, sortBy, sortDirection]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    if (!data) {
      return;
    }

    setSelectedLogId((current) => {
      if (current && data.entries.some((entry) => entry.id === current)) {
        return current;
      }

      return data.entries[0]?.id ?? null;
    });
  }, [data]);

  useEffect(() => {
    if (!selectedLogId) {
      setDetail(null);
      return;
    }

    let cancelled = false;
    setDetailLoading(true);

    getCronJobLogDetail(selectedLogId, environment)
      .then((response) => {
        if (!cancelled) {
          setDetail(response);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDetail(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDetailLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [environment, selectedLogId]);

  const selectedLog = useMemo(
    () => data?.entries.find((entry) => entry.id === selectedLogId),
    [data?.entries, selectedLogId],
  );

  const selectName = (name?: string) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("environment", environment);
    nextSearchParams.set("job", name ?? "all");
    setSearchParams(nextSearchParams);
    setPage(1);
  };

  const updateSortBy = (value: CronJobLogSortBy) => {
    setSortBy(value);
    setPage(1);
  };

  const updateSortDirection = (value: CronJobLogSortDirection) => {
    setSortDirection(value);
    setPage(1);
  };

  const updateRunFilter = (value: "all" | "true" | "false") => {
    setRunFilter(value);
    setPage(1);
  };

  const updateEnvironment = (value: CronJobLogEnvironment) => {
    setSelectedLogId(null);
    setDetail(null);
    setPage(1);

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("environment", value);
    nextSearchParams.set("job", "all");
    setSearchParams(nextSearchParams);
  };

  return (
    <div data-test="admin-monitoring-cron-jobs-panel" className="flex min-h-0 flex-1 overflow-hidden">
      <MonitoringNav
        selectedName={selectedName}
        jobTypes={data?.jobTypes ?? []}
        totalLogs={data?.summary.totalLogs ?? 0}
        onSelectName={selectName}
        canViewGeneric={canViewGeneric}
        canViewHoldCleaning={canViewHoldCleaning}
      />

      {isHoldCleaning ? (
        <div className={twMerge("min-w-0 flex-1 overflow-hidden", theme.home.tailwind.textDefault)}>
          <HoldCleaningMonitoringPanel />
        </div>
      ) : (
        <div className={twMerge("min-w-0 flex-1 overflow-hidden", theme.home.tailwind.textDefault)}>
          <main className={twMerge("flex h-full min-h-0 flex-col overflow-y-auto", adminStyles.page.pagePadding)}>
            <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className={adminStyles.heading.eyebrow}>Monitoring</p>
                <h1 className={twMerge("mt-1", adminStyles.heading.pageTitle, theme.home.tailwind.textDefault)}>
                  Cron job logs
                </h1>
              </div>
              <EnvironmentSelector value={environment} loading={loading} onChange={updateEnvironment} />
            </div>

            {errorMessage && (
              <div className="mb-4 border border-[#F59692] bg-[#FFEAED] px-4 py-3 text-sm text-[#BF0002]">
                {errorMessage}
              </div>
            )}

            {data ? (
              <div className="grid min-h-0 flex-1 gap-5 xl:grid-cols-[minmax(0,1fr)_460px]">
                <div className="min-w-0 space-y-5">
                  <DashboardTiles data={data} />
                  <LatestJobDashboard selectedLog={selectedLog ?? data.entries[0]} />
                  <TrendChart data={data} />
                  <Controls
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    runFilter={runFilter}
                    loading={loading}
                    onSortByChange={updateSortBy}
                    onSortDirectionChange={updateSortDirection}
                    onRunFilterChange={updateRunFilter}
                    onRefresh={() => void loadLogs()}
                  />
                  <LogList entries={data.entries} selectedLogId={selectedLogId} onSelectLog={setSelectedLogId} />
                  <Pagination data={data} onPageChange={setPage} />
                </div>

                <DetailPanel selectedLog={selectedLog} detail={detail} loading={detailLoading} />
              </div>
            ) : (
              <div className={adminStyles.card.loading}>
                <ArrowPathIcon className="mr-2 h-5 w-5 animate-spin text-green" />
                Loading cron job logs
              </div>
            )}
          </main>
        </div>
      )}
    </div>
  );
};

export default CronJobsMonitoringPanel;
